import {  useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Info,
  Camera,
  FileText,
  Hash,
  ShieldCheck,
  ChevronDown,
  Plus,
  Trash2,
  StickyNote,
  Loader2,
  AlertCircle,
  Check,
  Pencil,
} from "lucide-react";
import {
  ORIGIN,
  Field,
  Select,
  ImageUploader,
  useLookup,
} from "./VehicleFormUI";
import {
  PhotosModal,
  LicenseModal,
  PlateModal,
  InsuranceModal,
  PlatePreview,
} from "./VehicleModals";
import "./VehicleCreation.css";

const EMPTY_FORM = {
  vehicle_category_id: "",
  type_id: "",
  brand_id: "",
  model_id: "",
  year: "",
  is_primary: false,
  odometer_reading: "",
  vin: "",
  engine_number: "",
};

// when a field changes, these dependent fields get cleared
const RESETS = {
  vehicle_category_id: ["type_id", "model_id", "year"],
  type_id: ["model_id", "year"],
  brand_id: ["model_id", "year"],
  model_id: ["year"],
};

const EMPTY_NOTE = { title: "", description: "", note_type_id: "", images: [] };

const flattenErrors = (val) => {
  if (!val) return [];
  if (typeof val === "string") return [val];
  if (Array.isArray(val)) return val.flatMap(flattenErrors);
  if (typeof val === "object") return Object.values(val).flatMap(flattenErrors);
  return [String(val)];
};

/* ---------------- small presentational pieces ---------------- */
const Section = ({
  icon: Icon,
  title,
  subtitle,
  right,
  children,
  collapsible = false,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;

  return (
    <section className={`vc-section ${collapsible ? "is-collapsible" : ""}`}>
      <div
        className={`vc-section-head ${collapsible ? "is-clickable" : ""} ${
          isOpen ? "" : "is-closed"
        }`}
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpen((o) => !o);
                }
              }
            : undefined
        }
      >
        <div className="vc-section-head-left">
          {Icon && (
            <span className="vc-section-icon">
              <Icon size={18} />
            </span>
          )}
          <div>
            <h3>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
          </div>
        </div>

        <div className="vc-section-head-right">
          {right}
          {collapsible && (
            <ChevronDown
              size={18}
              className={`vc-section-chevron ${isOpen ? "is-open" : ""}`}
            />
          )}
        </div>
      </div>

      {isOpen && <div className="vc-section-body">{children}</div>}
    </section>
  );
};

const DocCard = ({ icon: Icon, title, desc, done, summary, onClick }) => (
  <button
    type="button"
    className={`vc-doc-card ${done ? "is-done" : ""}`}
    onClick={onClick}
  >
    <span className="vc-doc-card-icon">
      <Icon size={20} />
    </span>
    <div className="vc-doc-card-info">
      <strong>{title}</strong>
      <small>{done ? summary : desc}</small>
    </div>
    {done ? (
      <span className="vc-badge vc-badge--ok">
        <Check size={12} /> Provided
      </span>
    ) : (
      <span className="vc-badge vc-badge--warn">Not provided</span>
    )}
    <span className="vc-doc-card-edit">
      {done ? <Pencil size={14} /> : <Plus size={14} />}
    </span>
  </button>
);

/* ================================================================== */
export default function VehicleCreation({ clientId, onSuccess, onCancel }) {
  const { user } = useSelector((s) => s.auth);
  const token = user?.token;

  const [form, setForm] = useState(EMPTY_FORM);
  const [propValues, setPropValues] = useState({}); 
  const [photos, setPhotos] = useState([]);
  const [license, setLicense] = useState(null);
  const [plate, setPlate] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [notes, setNotes] = useState([]);

  const [modal, setModal] = useState(null); 
  const [openSections, setOpenSections] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);
  const bannerRef = useRef(null);

  /* ---------------- lookups ---------------- */
  const base = "/api/portal/users/vehicles";
  const categories = useLookup(`${base}/categories`, {
    pick: (d) => d?.vehicle_categories,
  });
  const brands = useLookup(`${base}/brands`, {
    pick: (d) => d?.vehicle_brands,
  });
  const types = useLookup(`${base}/types`, {
    params: { category_id: form.vehicle_category_id },
    enabled: !!form.vehicle_category_id,
    pick: (d) => d?.vehicle_types,
  });
  const models = useLookup(`${base}/models`, {
    params: { brand_id: form.brand_id, type_id: form.type_id },
    enabled: !!(form.brand_id && form.type_id),
    pick: (d) => d?.vehicle_models,
  });
  const propertySections = useLookup(`${base}/property-sections`, {
    pick: (d) => d?.property_sections,
  });
  const noteTypes = useLookup("/api/vehicle-note-types", {
    pick: (d) => d?.types,
  });

  const selectedModel = models.data.find(
    (m) => String(m.id) === String(form.model_id),
  );
  const yearOptions = useMemo(() => {
    if (!selectedModel) return [];
    let years = selectedModel.active_years;
    if (!years?.length && selectedModel.start_year) {
      const end = selectedModel.end_year || new Date().getFullYear();
      years = [];
      for (let y = selectedModel.start_year; y <= end; y++) years.push(y);
    }
    return [...(years || [])]
      .sort((a, b) => b - a)
      .map((y) => ({ value: y, label: String(y) }));
  }, [selectedModel]);

  /* ---------------- handlers ---------------- */
  const setField = (key, value) => {
    setForm((p) => {
      const next = { ...p, [key]: value };
      (RESETS[key] || []).forEach((r) => (next[r] = ""));
      return next;
    });
  };

  const updateNote = (i, key, value) =>
    setNotes((p) =>
      p.map((n, idx) => (idx === i ? { ...n, [key]: value } : n)),
    );

  /* ---------------- validation ---------------- */
  const required = {
    vehicle_category_id: "Category is required",
    brand_id: "Brand is required",
    type_id: "Type is required",
    model_id: "Model is required",
    year: "Year is required",
  };
  const fieldErr = (k) => (submitted && !form[k] ? required[k] : "");
  const noteErr = (n, k) => (submitted && !n[k] ? "Required" : "");
  const isValid =
    Object.keys(required).every((k) => form[k]) &&
    notes.every(
      (n) => n.title.trim() && n.description.trim() && n.note_type_id,
    );

  /* ---------------- build + submit ---------------- */
  const buildFormData = () => {
    const fd = new FormData();
    const add = (k, v) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    };

    // basic
    add("vehicle_category_id", form.vehicle_category_id);
    add("brand_id", form.brand_id);
    add("type_id", form.type_id);
    add("model_id", form.model_id);
    add("year", form.year);
    add("is_primary", form.is_primary ? 1 : 0);
    add("odometer_reading", form.odometer_reading);
    add("vin", form.vin);
    add("engine_number", form.engine_number);

    // dynamic properties
    let i = 0;
    Object.entries(propValues).forEach(([pid, vid]) => {
      if (!vid) return;
      fd.append(`properties[${i}][property_id]`, pid);
      fd.append(`properties[${i}][value_id]`, vid);
      i++;
    });

    // vehicle photos
    photos.forEach((f) => fd.append("profile_photos[]", f));

    // license
    if (license) {
      add("license_number", license.license_number);
      add("license_class_id", license.license_class_id);
      add("issue_date", license.issue_date);
      add("expiry_date", license.expiry_date);
      add("traffic_unit", license.traffic_unit);
      license.photos.forEach((f, idx) =>
        fd.append(`license_photos[${idx}]`, f),
      );
    }

    // plate
    if (plate) {
      add("plate_type_id", plate.plate_type_id);
      add("plate_right", plate.plate_right);
      add("plate_left", plate.plate_left);
      plate.photos.forEach((f) => fd.append("plate_photos[]", f));
    }

    // insurance
    if (insurance) {
      add("insurance_company_id", insurance.insurance_company_id);
      add("policy_type_id", insurance.policy_type_id);
      add("policy_number", insurance.policy_number);
            (insurance.coverage_type_ids || []).forEach((id, idx) =>
              fd.append(`coverage_type_ids[${idx}]`, id),
            );
      add("start_date", insurance.start_date);
      add("end_date", insurance.end_date);
      if (insurance.document)
        fd.append("insurance_document", insurance.document);
      insurance.images.forEach((f, idx) =>
        fd.append(`insurance_images[${idx}]`, f),
      );
    }

    // notes
    notes.forEach((n, idx) => {
      fd.append(`notes[${idx}][title]`, n.title);
      fd.append(`notes[${idx}][description]`, n.description);
      fd.append(`notes[${idx}][note_type_id]`, n.note_type_id);
      n.images.forEach((f, j) => fd.append(`notes[${idx}][images][${j}]`, f));
    });

    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setServerErrors([]);

    if (!isValid) {
      document
        .querySelector(".vc-root")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${ORIGIN}/api/portal/users/vehicles/create-new-vehicle/${clientId}`,
        buildFormData(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSuccess?.();
    } catch (err) {
      console.error("Error creating vehicle:", err);
       const data = err.response?.data;
       let list = flattenErrors(data?.errors);
       if (!list.length) list = flattenErrors(data?.message);
       if (!list.length)
         list = ["Something went wrong while saving the vehicle"];
       setServerErrors(list);
      setTimeout(
        () =>
          bannerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        50,
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- options ---------------- */
  const opt = (arr, label = "name") =>
    arr.map((x) => ({ value: x.id, label: x[label] }));

  /* ================================================================ */
  return (
    <form className="vc-root" onSubmit={handleSubmit} noValidate>
      <div className="vc-info-banner">
        <Info size={16} />
        <span>
          Adding vehicles helps us personalize services, recommend the right
          products and track maintenance history.
        </span>
        <span className="vc-required-note">
          <b>*</b> Required fields
        </span>
      </div>

      {serverErrors.length > 0 && (
        <div className="vc-alert" ref={bannerRef}>
          <AlertCircle size={18} />
          <ul>
            {serverErrors.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ------------------- Basic information ------------------- */}
      <Section
        icon={Info}
        title="Basic Information"
        subtitle="Start with the vehicle category — the other fields unlock step by step"
      >
        <div className="vc-grid vc-grid--4">
          <Field
            label="Vehicle Category"
            required
            error={fieldErr("vehicle_category_id")}
          >
            <Select
              value={form.vehicle_category_id}
              options={opt(categories.data)}
              loading={categories.loading}
              placeholder="Select category"
              invalid={!!fieldErr("vehicle_category_id")}
              onChange={(v) => setField("vehicle_category_id", v)}
            />
          </Field>

          <Field label="Type" required error={fieldErr("type_id")}>
            <Select
              value={form.type_id}
              options={opt(types.data)}
              loading={types.loading}
              disabled={!form.vehicle_category_id}
              placeholder="Select type"
              invalid={!!fieldErr("type_id")}
              onChange={(v) => setField("type_id", v)}
            />
          </Field>

          <Field label="Brand" required error={fieldErr("brand_id")}>
            <Select
              value={form.brand_id}
              options={opt(brands.data)}
              loading={brands.loading}
              disabled={!form.vehicle_category_id}
              placeholder="Select brand"
              invalid={!!fieldErr("brand_id")}
              onChange={(v) => setField("brand_id", v)}
            />
          </Field>

          <Field label="Model" required error={fieldErr("model_id")}>
            <Select
              value={form.model_id}
              options={opt(models.data)}
              loading={models.loading}
              disabled={!form.brand_id || !form.type_id}
              placeholder="Select model"
              invalid={!!fieldErr("model_id")}
              onChange={(v) => setField("model_id", v)}
            />
          </Field>

          <Field label="Year" required error={fieldErr("year")}>
            <Select
              value={form.year}
              options={yearOptions}
              disabled={!form.model_id}
              placeholder="Select year"
              invalid={!!fieldErr("year")}
              onChange={(v) => setField("year", v)}
            />
          </Field>

          <Field label="Mileage (KM)">
            <div className="vc-input-suffix">
              <input
                type="number"
                min="0"
                className="vc-input"
                placeholder="e.g. 48250"
                value={form.odometer_reading}
                onChange={(e) => setField("odometer_reading", e.target.value)}
              />
              <span>KM</span>
            </div>
          </Field>

          <Field label="VIN / Chassis Number">
            <input
              type="text"
              className="vc-input"
              placeholder="Enter VIN / chassis number"
              value={form.vin}
              onChange={(e) => setField("vin", e.target.value)}
            />
          </Field>

          <Field label="Engine Number">
            <input
              type="text"
              className="vc-input"
              placeholder="Enter engine number (optional)"
              value={form.engine_number}
              onChange={(e) => setField("engine_number", e.target.value)}
            />
          </Field>
        </div>

        <label
          className={`vc-primary-toggle ${form.is_primary ? "is-on" : ""}`}
        >
          <input
            type="checkbox"
            checked={form.is_primary}
            onChange={(e) => setField("is_primary", e.target.checked)}
          />
          <span className="vc-checkbox">
            <Check size={13} />
          </span>
          <div>
            <strong>Set as primary vehicle</strong>
            <small>
              This vehicle will be selected by default for new orders
            </small>
          </div>
        </label>
      </Section>

      {/* ------------------- Dynamic properties ------------------- */}
      {propertySections.loading && (
        <div className="vc-loading-row">
          <Loader2 size={16} className="vc-spin" /> Loading vehicle
          properties...
        </div>
      )}

      {propertySections.data.map((sec, idx) => {
        const props = (sec.properties || []).filter((p) => p.values?.length);
        if (!props.length) return null;
        const filled = props.filter((p) => propValues[p.id]).length;
        const open = openSections[sec.id] ?? idx === 0;

        return (
          <div className={`vc-accordion ${open ? "is-open" : ""}`} key={sec.id}>
            <button
              type="button"
              className="vc-accordion-head"
              onClick={() =>
                setOpenSections((p) => ({
                  ...p,
                  [sec.id]: !(p[sec.id] ?? idx === 0),
                }))
              }
            >
              <span className="vc-section-icon">
                {sec.icon_url ? (
                  <img
                    src={`${ORIGIN}${sec.icon_url}`}
                    alt=""
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  sec.name_en?.[0]
                )}
              </span>
              <div className="vc-accordion-title">
                <strong>{sec.name_en}</strong>
                <small>{props.length} properties</small>
              </div>
              <span
                className={`vc-badge ${filled ? "vc-badge--ok" : "vc-badge--warn"}`}
              >
                {filled ? `${filled}/${props.length} filled` : "Not provided"}
              </span>
              <ChevronDown size={18} className="vc-accordion-chevron" />
            </button>

            {open && (
              <div className="vc-accordion-body">
                <div className="vc-grid vc-grid--4">
                  {props.map((p) => (
                    <Field label={p.name} key={p.id}>
                      <Select
                        clearable
                        value={propValues[p.id] || ""}
                        placeholder={`Select ${p.name.toLowerCase()}`}
                        options={p.values.map((v) => ({
                          value: v.id,
                          label: v.value,
                        }))}
                        onChange={(v) =>
                          setPropValues((prev) => ({ ...prev, [p.id]: v }))
                        }
                      />
                    </Field>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ------------------- Photos & documents ------------------- */}
      <Section
        icon={Camera}
        title="Photos & Documents"
        subtitle="Add vehicle photos, license, plate and insurance"
        collapsible
        defaultOpen={false}
        right={(() => {
          const provided = [
            photos.length > 0,
            !!license,
            !!plate,
            !!insurance,
          ].filter(Boolean).length;
          return (
            <span
              className={`vc-badge ${provided ? "vc-badge--ok" : "vc-badge--warn"}`}
            >
              {provided ? `${provided}/4 provided` : "Not provided"}
            </span>
          );
        })()}
      >
        <div className="vc-doc-grid">
          <DocCard
            icon={Camera}
            title="Vehicle Photos"
            desc="Upload photos of the vehicle"
            done={photos.length > 0}
            summary={`${photos.length} photo${photos.length > 1 ? "s" : ""} added`}
            onClick={() => setModal("photos")}
          />
          <DocCard
            icon={FileText}
            title="License"
            desc="Add vehicle license details"
            done={!!license}
            summary={
              license?.license_number
                ? `No. ${license.license_number}`
                : "License details added"
            }
            onClick={() => setModal("license")}
          />
          <DocCard
            icon={Hash}
            title="Plate"
            desc="Add the plate type and number"
            done={!!plate}
            summary={`${plate?.type?.name_en || ""} • ${plate?.plate_left || ""} ${
              plate?.plate_right || ""
            }`}
            onClick={() => setModal("plate")}
          />
          <DocCard
            icon={ShieldCheck}
            title="Insurance"
            desc="Add insurance policy details"
            done={!!insurance}
            summary={
              insurance?.policy_number
                ? `Policy ${insurance.policy_number}`
                : "Insurance details added"
            }
            onClick={() => setModal("insurance")}
          />
        </div>

        {plate && (
          <div className="vc-plate-inline">
            <PlatePreview
              type={plate.type}
              left={plate.plate_left}
              right={plate.plate_right}
              small
            />
          </div>
        )}
      </Section>

      {/* ------------------- Notes ------------------- */}
      <Section
        icon={StickyNote}
        title="Notes"
        subtitle="Add notes about the vehicle (optional)"
        right={
          <button
            type="button"
            className="vc-btn vc-btn--outline"
            onClick={() => setNotes((p) => [...p, { ...EMPTY_NOTE }])}
          >
            <Plus size={15} /> Add Note
          </button>
        }
      >
        {notes.length === 0 && (
          <div className="vc-empty">
            <StickyNote size={22} />
            <p>No notes yet. Click “Add Note” to create one.</p>
          </div>
        )}

        <div className="vc-notes">
          {notes.map((n, i) => (
            <div className="vc-note" key={i}>
              <div className="vc-note-head">
                <strong>Note {i + 1}</strong>
                <button
                  type="button"
                  className="vc-icon-btn vc-icon-btn--danger"
                  onClick={() =>
                    setNotes((p) => p.filter((_, idx) => idx !== i))
                  }
                  title="Remove note"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="vc-grid vc-grid--2">
                <Field label="Title" required error={noteErr(n, "title")}>
                  <input
                    type="text"
                    className={`vc-input ${noteErr(n, "title") ? "is-invalid" : ""}`}
                    placeholder="Note title"
                    value={n.title}
                    onChange={(e) => updateNote(i, "title", e.target.value)}
                  />
                </Field>
                <Field
                  label="Note Type"
                  required
                  error={noteErr(n, "note_type_id")}
                >
                  <Select
                    value={n.note_type_id}
                    loading={noteTypes.loading}
                    placeholder="Select note type"
                    invalid={!!noteErr(n, "note_type_id")}
                    options={opt(noteTypes.data, "title")}
                    onChange={(v) => updateNote(i, "note_type_id", v)}
                  />
                </Field>
                <Field
                  label="Description"
                  required
                  error={noteErr(n, "description")}
                  className="vc-col-full"
                >
                  <textarea
                    rows={3}
                    className={`vc-input vc-textarea ${
                      noteErr(n, "description") ? "is-invalid" : ""
                    }`}
                    placeholder="Add details about this note"
                    value={n.description}
                    onChange={(e) =>
                      updateNote(i, "description", e.target.value)
                    }
                  />
                </Field>
                <Field label="Images" className="vc-col-full">
                  <ImageUploader
                    compact
                    max={6}
                    files={n.images}
                    onChange={(f) => updateNote(i, "images", f)}
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ------------------- Actions bar ------------------- */}
      <div className="vc-actions-bar">
        {onCancel && (
          <button
            type="button"
            className="vc-btn vc-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="vc-btn vc-btn--primary vc-btn--lg"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="vc-spin" /> Saving...
            </>
          ) : (
            "Save Vehicle"
          )}
        </button>
      </div>

      {/* ------------------- Modals ------------------- */}
      {modal === "photos" && (
        <PhotosModal
          value={photos}
          onSave={setPhotos}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "license" && (
        <LicenseModal
          value={license}
          onSave={setLicense}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "plate" && (
        <PlateModal
          value={plate}
          onSave={setPlate}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "insurance" && (
        <InsuranceModal
          value={insurance}
          onSave={setInsurance}
          onClose={() => setModal(null)}
        />
      )}
    </form>
  );
}
